import { PathComposition, type PathCompositionItemProps, truthy } from "@quortex/front-common"
import { isEmpty } from "lodash-es"
import type { FC } from "react"
import { extensionByPackagingType } from "src/App/play/components/features/packaging/helpers"
import type { EndpointComposition } from "@/App/play/App/Main/Pool/endpoints/helpers"

interface Props {
  className?: string
  "data-testid"?: string
  disabled?: boolean
  endpoint: EndpointComposition
  highlight?: "input" | "processing" | "packaging"
  onClick?: (url: string) => void
  processingType?: api.ott.Pool["type"]
  searchRegExps?: RegExp[]
  size?: "small" | "x-small"
  strikethrough?: boolean
  wrap?: boolean
  dns?: string
}

const PlayPathComposition: FC<Props> = ({
  className,
  "data-testid": dataTestId,
  disabled,
  endpoint,
  highlight,
  onClick,
  processingType,
  searchRegExps = [],
  size,
  strikethrough,
  wrap,
  dns
}) => {
  const customPath = !!endpoint.customPath
  const advanced = processingType === "premium"
  let lineupSelectorIdentifier: string = ""
  let extension: string = `(${Object.values(extensionByPackagingType).join("|")})`

  if (!customPath) {
    if (endpoint.pretty) {
      // if "pretty" is present, use this as it may contain a reference to the
      // lineup selector identifier that is not available elsewhere.
      extension = endpoint.pretty.endsWith(".mpd") ? "mpd" : "m3u8"
      const url_parts = endpoint.pretty
        .substring(0, endpoint.pretty.indexOf(`.${extension}`))
        .split("/")
      if (advanced) {
        lineupSelectorIdentifier = url_parts[2] ?? ""
      }
    } else {
      lineupSelectorIdentifier = "..."
      if (endpoint.target?.type) {
        extension = extensionByPackagingType[endpoint.target.type]
      }
    }
  }

  const pathPart = {
    dns,
    extension: extension || `(${Object.values(extensionByPackagingType).join("|")})`,
    input: endpoint.input?.identifier ?? "",
    lineupSelector: lineupSelectorIdentifier,
    processing: endpoint.processing?.identifier ?? "",
    target: endpoint.target?.identifier ?? ""
  }

  const pathProps = buildPaths(
    processingType === "premium",
    pathPart.target,
    pathPart.input,
    pathPart.processing,
    pathPart.lineupSelector,
    pathPart.dns,
    pathPart.extension
  )
  const uri = pathProps.map(p => `${p.separator || ""}${p.path}`).join("")

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: 6
    <div
      className={className}
      data-testid={dataTestId}
      role="definition"
      onClick={() => onClick?.(uri)}
    >
      <InnerPlayPathComposition
        disabled={disabled}
        dns={pathPart.dns}
        extension={pathPart.extension}
        highlight={highlight}
        inputPath={pathPart.input}
        isCustomPath={customPath}
        lineupSelector={pathPart.lineupSelector}
        processingPath={pathPart.processing}
        processingType={processingType}
        searchRegExps={searchRegExps}
        size={size}
        strikethrough={strikethrough}
        targetPath={pathPart.target}
        wrap={wrap}
      />
    </div>
  )
}

const buildPaths = (
  advanced: boolean,
  targetPath: string,
  inputPath: string,
  processingPath: string,
  lineupSelector?: string,
  dns?: string,
  extension?: string,
  highlight?: "input" | "processing" | "packaging"
): PathCompositionItemProps[] => {
  const processingIdentifier =
    advanced && lineupSelector && processingPath
      ? lineupSelector.split(`-${processingPath}`)[0]
      : ""
  return [
    !isEmpty(dns) && {
      color: "black",
      path: dns,
      separator: ""
    },
    {
      color: advanced ? "teal" : "pink",
      highlighted: highlight === (advanced ? "packaging" : "input"),
      path: advanced ? targetPath : inputPath,
      separator: "/"
    },
    {
      color: advanced ? "pink" : "purple",
      highlighted: highlight === (advanced ? "input" : "processing"),
      path: advanced
        ? !isEmpty(processingIdentifier)
          ? `${inputPath}-${processingIdentifier}`
          : inputPath
        : processingPath,
      separator: "/"
    },
    advanced &&
      !isEmpty(lineupSelector) && {
        color: "grey",
        path: lineupSelector,
        separator: "/"
      },
    {
      color: advanced ? "purple" : "teal",
      highlighted: highlight === (advanced ? "processing" : "packaging"),
      path: advanced ? processingPath : targetPath,
      separator: "/"
    },
    {
      color: "grey",
      path: `${advanced ? "" : "index"}.${extension}`,
      separator: advanced ? "" : "/"
    }
  ].filter(truthy) as PathCompositionItemProps[]
}

interface InnerPlayPathCompositionProps {
  isCustomPath: boolean
  processingType: api.ott.Pool["type"]
  inputPath: string
  processingPath: string
  targetPath: string
  extension: string
  lineupSelector?: string
  searchRegExps?: RegExp[]
  size?: "small" | "x-small"
  disabled?: boolean
  strikethrough?: boolean
  wrap?: boolean
  highlight?: "input" | "processing" | "packaging"
  dns?: string
}

const InnerPlayPathComposition: FC<InnerPlayPathCompositionProps> = ({
  isCustomPath,
  processingType,
  inputPath,
  processingPath,
  targetPath,
  extension,
  lineupSelector,
  searchRegExps,
  size = "small",
  disabled,
  strikethrough,
  wrap,
  highlight,
  dns
}) => {
  const advanced = processingType === "premium"

  return (
    <PathComposition
      disabled={disabled}
      isCustomPath={isCustomPath}
      paths={buildPaths(
        advanced,
        targetPath,
        inputPath,
        processingPath,
        lineupSelector,
        dns,
        extension,
        highlight
      )}
      searchRegExps={searchRegExps}
      size={size}
      strikethrough={strikethrough}
      wrap={wrap}
    />
  )
}

export default PlayPathComposition
